import { useEffect, useMemo, useRef, useState } from "react";

// Stabile Leer-Werte, damit `x || []`-Fallbacks keine neuen Referenzen pro Render erzeugen.
const EMPTY_LIST = [];
const EMPTY_OBJ = {};
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { X, ClipboardCheck, Sparkles, PlaneTakeoff, Check, Loader2, XCircle, Edit3, ShieldCheck, StickyNote, ChevronDown, AlertTriangle, Plus, Eye, UserCheck, HeartHandshake, Printer } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { effectiveBookingStatus, BOOKING_PILL, BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import NoteTemplatesRow from "./NoteTemplatesRow";
import BookingStatusHistory from "./BookingStatusHistory";
import { BookingPrepPreferencesCard } from "./BookingPrepPreferencesCard";
import { useAdminChecklistPin } from "./AdminChecklistPinDialog";
import { BookingChecklistAuditPanel } from "./customer-detail/BookingChecklistAuditPanel";
import { BookingDateAuditPanel } from "./customer-detail/BookingDateAuditPanel";
import CustomerPinPromptDialog from "./customer/CustomerPinPromptDialog";
import { deDateLong, deDateMedium, deDateShort, deDateTime, enDateLong } from "../lib/dateFormat";
import { BookingHeaderAssigneeLines, BookingAssigneePicker, TeamReadOnlyCustomerChecklist, DE_LONG, WelcomeBackNoteEditor, TeamServicesChecklist, CancelConfirmation, BookingEditForm, ChecklistSection } from "./BookingDetailParts";
import { useMarkSeen, SeenStatusLine } from "./SeenIndicator";


/**
 * Booking detail overlay for the customer:
 * shows the date range, checklists (interactive), booked extras,
 * and lets the customer request a change or cancellation.
 *
 * Status is derived via `effectiveBookingStatus(view)` so it always matches
 * the Balken on the home screen — no more "modal reset to Angefragt" bug.
 */
export default function BookingDetailModal({ booking, extras, propertyName, onClose, onChanged, mode = "customer" }) {
  const { user, lang: _langAuth } = useAuth();
  // Feb 2026 · Admin-PIN-Gate für Checklisten-Bearbeitung (1 Sep 2026,
  // erweitert 11 Sep 2026): Der Prompt greift jetzt für ALLE Team-Rollen
  // (admin + concierge) — jede Checklisten-Änderung im Team-Modus muss
  // per PIN bestätigt werden. Kunden (mode="customer") bleiben unberührt.
  // `getPinHeaders` liefert ein Promise, das die Header-Objekte für die
  // PATCH-Calls zurückgibt (oder null bei Abbruch). Der Dialog steckt am
  // Modal-Ende (`adminPinDialog`).
  const isAdmin = user?.role === "admin";
  const isTeam = mode === "team" && (user?.role === "admin" || user?.role === "concierge");
  const seenNow = useMarkSeen("booking", booking?.id, mode === "team");
  const {
    getPinHeaders: getAdminPinHeaders,
    clearPin: clearAdminPin,
    dialog: adminPinDialog,
  } = useAdminChecklistPin({ enabled: isTeam });

  // Feb 2026 · 11 Sep 2026 · Global-Save-Bar-Refactor.
  // Alle Team-Änderungen (Checkliste, Verantwortlicher, Datum + Services,
  // Notizen) werden auf Modal-Ebene gepuffert und mit EINEM PIN-Prompt
  // per Batch persistiert. Kein einzelnes Editor-Component fährt mehr
  // eigene Netzwerk-Calls; sie schreiben lediglich in `pending`.
  //
  // Struktur:
  //   pending.checklist   = { completed, services, disabled_std } | jeweils Array | null
  //   pending.assigned_to = { value } | null   (Wrapper, damit `null` von „unset" unterschieden werden kann)
  //   pending.dates       = { from_date, to_date, services, disabled_standard_services, service_notes, notes } | null
  //
  // Concierge-Modus wirkt gleichfalls — der Backend-Guard prüft weiterhin
  // JEDEN PATCH (siehe `_require_admin_checklist_pin`).
  const [pendingChecklist, setPendingChecklist] = useState({ completed: null, services: null, disabled_std: null });
  const [pendingAssignee, setPendingAssignee] = useState(null);
  const [pendingDates, setPendingDates] = useState(null);
  const [globalSaving, setGlobalSaving] = useState(false);
  // Feb 2026 · 16 Sep 2026 · User-Directive „Customer-PIN in Customer View":
  // Kunden bestätigen jede Buchungsänderung mit ihrer persönlichen 4-stelligen
  // PIN (dieselbe wie für Angebots-Signatur). Backend erwartet `customer_pin`
  // im Body von PATCH /bookings/{id}. Fehlt die PIN (428) → Setup-Modal;
  // falsche PIN (403) → Retry im selben Dialog.
  const [customerPinIsSet, setCustomerPinIsSet] = useState(null);   // null = unbekannt
  const [customerPinPromptMode, setCustomerPinPromptMode] = useState(null); // null | "confirm" | "setup"
  // Feb 2026 · Inline-Overlap-Warnung: HTTP 409 vom Backend wird nicht
  // nur getoastet, sondern als persistenter Konflikt-Kontext in der
  // Save-Bar + roter Umrandung der Datums-Inputs sichtbar gemacht.
  // Wird gecleart, sobald das Datum-Puffer verändert oder das Modal auf
  // eine andere Buchung geswitcht wird.
  const [globalOverlapError, setGlobalOverlapError] = useState(null);
  useEffect(() => {
    setPendingChecklist({ completed: null, services: null, disabled_std: null });
    setPendingAssignee(null);
    setPendingDates(null);
    setGlobalOverlapError(null);
  }, [booking?.id]);
  useEffect(() => {
    setGlobalOverlapError(null);
  }, [pendingDates?.from_date, pendingDates?.to_date]);
  // Audit-Log-Refresh-Token: erhöht sich bei jedem erfolgreichen Toggle,
  // damit der Panel-useEffect neu lädt und die Zeile sofort sichtbar wird.
  const [auditRefresh, setAuditRefresh] = useState(0);
  const bumpAudit = () => setAuditRefresh((n) => n + 1);
  const { lang } = useI18n();
  // Admin dashboard remains German; only translate for customers with EN toggle.
  const de = mode === "team" || lang !== "en";
  const [editOpen, setEditOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  // Feb 2026 · 17 Sep 2026 · Bugfix „Datum ändern unsichtbar hinter Sticky-Header":
  // Das Edit-Formular liegt im `sticky bottom-0` Footer; sobald es aufklappt,
  // wird der Container ~2500px hoch und CSS-Sticky ankert ihn dann an den
  // Container-Anfang, wo er hinter dem sticky-top-Header (z-index:10) versteckt
  // wird. Fix: sticky wird beim Bearbeiten aufgehoben (siehe conditional
  // className unten) und wir scrollen die Zeitraum-Sektion sofort in den
  // sichtbaren Bereich, damit die Datums-Inputs oben stehen.
  const editSectionRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [standardServices, setStandardServices] = useState([]);
  // Sync local snapshot on every incoming booking (id OR status change) —
  // bewusst NICHT bei jedem Poll, damit ungespeicherte Checklisten-Edits in
  // `local` nicht überschrieben werden. Aktuelles `booking` via Ref lesen.
  const bookingRef = useRef(booking);
  useEffect(() => { bookingRef.current = booking; });
  const [local, setLocal] = useState(booking);
  useEffect(() => { setLocal(bookingRef.current); }, [booking?.id, booking?.status]);
  const view = local || booking;

  // Load Inklusiv-Leistungen once — shared between view + edit form.
  useEffect(() => {
    let mounted = true;
    api.get("/standard-services")
      .then((r) => { if (mounted) setStandardServices(r.data || []); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Feb 2026 · 16 Sep 2026 · Customer-PIN-Status einmal pro Modal-Öffnung
  // laden (nur Customer-Modus). Team-User laufen weiterhin über den
  // persönlichen Team-PIN-Flow (X-Admin-Pin-Header).
  useEffect(() => {
    if (mode === "team") return;
    let mounted = true;
    api.get("/me/customer-pin/status")
      .then((r) => { if (mounted) setCustomerPinIsSet(!!r.data?.is_set); })
      .catch(() => { if (mounted) setCustomerPinIsSet(false); });
    return () => { mounted = false; };
  }, [mode]);

  // Auto-scroll das Edit-Formular in den sichtbaren Bereich, sobald es
  // aufgeklappt wird. Ohne das Scrollen würde der Kunde nach dem Klick auf
  // „Termin ändern" nur den bisherigen Body sehen und die Datums-Inputs
  // (oben im Formular) übersehen — genau der ronaldstuyvesant-Bug.
  //
  // Wir scrollen den nächsten Overflow-Container (das ist die
  // `.overflow-y-auto`-Modal-Hülle) manuell auf den Editor, mit einem
  // Offset in Höhe des sticky-top-Headers. Sonst würde der Editor unter
  // dem festen Header verschwinden (137px Header verdeckt Zeitraum-Zeile).
  useEffect(() => {
    if (!editOpen) return;
    const el = editSectionRef.current;
    if (!el) return;
    const scroller = el.closest(".overflow-y-auto");
    if (!scroller) return;
    // Doppelter rAF, damit React die Layout-Änderung bereits committed hat.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const stickyHeader = scroller.querySelector(".sticky.top-0");
        const headerH = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
        const target = el.offsetTop - headerH - 8;
        try {
          scroller.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
        } catch {
          scroller.scrollTop = Math.max(0, target);
        }
      });
    });
  }, [editOpen]);

  const effStatus = effectiveBookingStatus(view);
  const meta = BOOKING_PILL[effStatus] || BOOKING_PILL.requested;
  const nights = useMemo(() => {
    if (!view?.from_date || !view?.to_date) return 0;
    return Math.max(0, Math.round((new Date(view.to_date) - new Date(view.from_date)) / 86400000));
  }, [view?.from_date, view?.to_date]);

  const extrasNames = useMemo(() => {
    const byId = Object.fromEntries((extras || []).map((e) => [e.id, e]));
    return (view?.services || [])
      .map((id) => byId[id])
      .filter(Boolean);
  }, [view?.services, extras]);

  const serviceNotes = view?.service_notes || EMPTY_OBJ;
  const disabledStd = view?.disabled_standard_services || EMPTY_LIST;
  const disabledStdItems = useMemo(() => {
    const byKey = Object.fromEntries((standardServices || []).map((s) => [s.key, s]));
    return disabledStd.map((k) => byKey[k]).filter(Boolean);
  }, [disabledStd, standardServices]);

  const submitCancel = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/cancel`);
      toast.success(de ? "Termin storniert · Ihr Concierge wurde informiert." : "Booking cancelled · your concierge has been notified.", { duration: 4500 });
      setLocal(data);
      setCancelConfirmOpen(false);
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setSending(false); }
  };

  // Feb 2026 · 11 Sep 2026 · User-Directive „PIN einmal je Modal-Öffnung"
  // (bis 16 Sep 2026). Ab 16 Sep 2026 wird die PIN bei JEDEM Save frisch
  // gefragt (kein Session-Cache mehr) — `clearAdminPin` bleibt trotzdem
  // als defensiver Cleanup beim Modal-Unmount erhalten.
  useEffect(() => {
    return () => {
      try { clearAdminPin?.(); } catch { /* best-effort */ }
    };
  }, [clearAdminPin]);

  // Team-Sicht: sobald ein Concierge die Buchung im Modal öffnet, stempeln
  // wir sie mit `seen_by_team_at`. Der Kunde sieht daraufhin neben jedem
  // Punkt seiner privaten Checkliste einen kleinen „Team · vor 5 Min"-Marker.
  // Feuert einmalig pro Modal-Öffnung (Deps: booking.id + mode).
  // MUST stay above the early return to respect React's rules-of-hooks —
  // the guard is inside the effect body, not around the hook call.
  useEffect(() => {
    if (!booking?.id || mode !== "team") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/team/bookings/${booking.id}/mark-seen`);
        if (cancelled) return;
        // Reflect the stamp locally so it's immediately visible if the
        // team-side ever renders private checklists in the same session.
        setLocal((b) => (b ? { ...b, ...data } : b));
      } catch { /* silent — non-critical UX signal */ }
    })();
    return () => { cancelled = true; };
  }, [booking?.id, mode]);

  if (!booking) return null;

  const dep = view?.departure_checklist || [];
  const arr = view?.arrival_checklist || [];

  // Feb 2026 · 11 Sep 2026 · Global-Save-Bar-Refactor. Aggregierte Pending-
  // Zähler + eine gemeinsame `handleGlobalSave`-Funktion, die alle
  // gepufferten Änderungen (Checkliste, Verantwortlicher, Termin-Editor)
  // mit einem einzigen PIN-Prompt persistiert.
  const diffCount = (a, b) => {
    if (a === null || a === undefined) return 0;
    const p = new Set(a);
    const s = new Set(b || []);
    let n = 0;
    for (const k of p) if (!s.has(k)) n++;
    for (const k of s) if (!p.has(k)) n++;
    return n;
  };
  const checklistCount =
    diffCount(pendingChecklist.completed, view?.completed_services) +
    diffCount(pendingChecklist.services, view?.services) +
    diffCount(pendingChecklist.disabled_std, view?.disabled_standard_services);
  const assigneeCount = pendingAssignee !== null && pendingAssignee?.value !== (view?.assigned_to || "") ? 1 : 0;
  // Termin-Editor: wenn Pending-Set existiert, zählen wir die tatsächlich
  // geänderten Felder gegenüber der Server-View — nicht die Anzahl der
  // übermittelten Felder, sonst zählen wir jede Wiederöffnung.
  const datesCount = (() => {
    if (!pendingDates) return 0;
    const p = pendingDates;
    const b = view || {};
    let n = 0;
    if (p.from_date !== b.from_date) n++;
    if (p.to_date !== b.to_date) n++;
    if ((p.notes || "") !== (b.notes || "")) n++;
    // services / disabled_standard_services / service_notes: strukturelle Diffs
    const set = (x) => new Set(x || []);
    const sameArr = (x, y) => {
      const a = set(x), c = set(y);
      if (a.size !== c.size) return false;
      for (const v of a) if (!c.has(v)) return false;
      return true;
    };
    if (!sameArr(p.services, b.services)) n++;
    if (!sameArr(p.disabled_standard_services, b.disabled_standard_services)) n++;
    // service_notes: nur wenn dict-Keys/Values divergieren
    const sn1 = p.service_notes || {}, sn2 = b.service_notes || {};
    const k1 = Object.keys(sn1), k2 = Object.keys(sn2);
    let notesDiff = k1.length !== k2.length;
    if (!notesDiff) {
      for (const k of k1) if ((sn1[k] || "") !== (sn2[k] || "")) { notesDiff = true; break; }
    }
    if (notesDiff) n++;
    return n;
  })();
  const totalPending = checklistCount + assigneeCount + datesCount;

  const handleGlobalSave = async () => {
    if (totalPending === 0 || globalSaving) return;
    const headers = await getAdminPinHeaders();
    if (headers === null) {
      toast.error("Änderungen nicht gespeichert — PIN-Eingabe abgebrochen.");
      return;
    }
    setGlobalSaving(true);
    // Feb 2026 · 11 Sep 2026 · Undo-Toast-Refactor.
    // Snapshot des Server-Zustands VOR dem Save — brauchen wir, um beim
    // Klick auf „Rückgängig" die vorherigen Werte zurückzupatschen.
    // ID separat capturen, damit ein Modal-Wechsel den Undo nicht auf
    // eine andere Buchung schießt.
    const undoBookingId = view.id;
    const snapshot = {
      completed_services: [...(view?.completed_services || [])],
      services: [...(view?.services || [])],
      disabled_standard_services: [...(view?.disabled_standard_services || [])],
      from_date: view?.from_date || null,
      to_date: view?.to_date || null,
      notes: view?.notes || "",
      service_notes: { ...(view?.service_notes || {}) },
      assigned_to: view?.assigned_to || null,
    };
    let weeklyDraftToastShown = false;
    let latest = null;
    try {
      // 1) Completed-Services deltas (eigener Endpoint pro service_key).
      if (pendingChecklist.completed !== null) {
        const serverSet = new Set(view?.completed_services || []);
        const effSet = new Set(pendingChecklist.completed);
        const toRemove = [...serverSet].filter((k) => !effSet.has(k));
        const toAdd = [...effSet].filter((k) => !serverSet.has(k));
        for (const k of toRemove) {
          const { data } = await api.patch(
            `/team/bookings/${view.id}/completed-services`,
            { service_key: k, done: false },
            { headers },
          );
          latest = data;
        }
        for (const k of toAdd) {
          const { data } = await api.patch(
            `/team/bookings/${view.id}/completed-services`,
            { service_key: k, done: true },
            { headers },
          );
          latest = data;
          if (data?._weekly_draft_created && !weeklyDraftToastShown) {
            toast.success("Termin erledigt · Wochenbericht-Entwurf wurde erstellt.", { duration: 5000 });
            weeklyDraftToastShown = true;
          }
        }
      }
      // 2) Konsolidiertes PATCH /bookings/{id} mit allen anderen Feldern.
      //    Termin-Editor-Werte überschreiben ggf. Checklist-Überlappungen
      //    (er ist die spezifischere UI für Datum + services + notes).
      const payload = {};
      if (pendingChecklist.services !== null) payload.services = pendingChecklist.services;
      if (pendingChecklist.disabled_std !== null) payload.disabled_standard_services = pendingChecklist.disabled_std;
      if (pendingDates) {
        if (pendingDates.from_date !== undefined) payload.from_date = pendingDates.from_date;
        if (pendingDates.to_date !== undefined) payload.to_date = pendingDates.to_date;
        if (pendingDates.notes !== undefined) payload.notes = pendingDates.notes;
        if (pendingDates.services !== undefined) payload.services = pendingDates.services;
        if (pendingDates.disabled_standard_services !== undefined) payload.disabled_standard_services = pendingDates.disabled_standard_services;
        if (pendingDates.service_notes !== undefined) payload.service_notes = pendingDates.service_notes;
      }
      if (pendingAssignee !== null) {
        payload.assigned_to = pendingAssignee.value || null;
      }
      if (Object.keys(payload).length > 0) {
        try {
          const { data } = await api.patch(`/bookings/${view.id}`, payload, { headers });
          latest = data;
        } catch (err) {
          if (err.response?.status === 409) {
            // Feb 2026 · Datum-Overlap: Puffer bleibt erhalten, zusätzlich
            // wird die Konflikt-Range persistent in Save-Bar + Termin-
            // Editor angezeigt (roter Rand + „Konflikt vom … → …"-Zeile).
            const detail = err.response?.data?.detail || {};
            const msg = (typeof detail === "object" && detail.message) || formatErr(detail);
            setGlobalOverlapError({
              from: detail?.conflict_from_date || null,
              to: detail?.conflict_to_date || null,
            });
            toast.error(msg || "Zeitraum überlappt mit einer bestehenden Abwesenheit.");
            return;
          }
          throw err;
        }
      }
      if (latest) setLocal((cur) => ({ ...(cur || {}), ...latest }));
      const total = totalPending;
      const postSaveCompleted = new Set(latest?.completed_services || snapshot.completed_services);
      setPendingChecklist({ completed: null, services: null, disabled_std: null });
      setPendingAssignee(null);
      setPendingDates(null);
      setGlobalOverlapError(null);
      onChanged?.();
      bumpAudit();

      // Feb 2026 · 11 Sep 2026 · Undo-Aktion. 5 Sekunden Zeitfenster.
      // Feb 2026 · 16 Sep 2026 · User-Directive „PIN jedes Mal fragen":
      // Undo triggert wie jeder Save eine frische PIN-Prompt (kein
      // Session-Cache mehr). Rollt sowohl completed-services-Deltas
      // als auch die konsolidierten Felder auf den Snapshot-Zustand
      // zurück.
      const undoFn = async () => {
        // Doppelklick-Guard: sobald der Toast weg ist, blockt das
        // globalSaving-Flag ohnehin — hier zusätzlich hart against
        // gleichzeitige Klicks.
        if (undoBookingId !== view.id && undoBookingId !== booking?.id) {
          // Modal zeigt inzwischen eine andere Buchung → still on the
          // captured id fahren, aber ohne UI-Update auf die aktuelle
          // Ansicht (die betrifft die Buchung ja nicht mehr).
        }
        setGlobalSaving(true);
        try {
          const undoHeaders = await getAdminPinHeaders();
          if (undoHeaders === null) {
            toast.error("Undo abgebrochen — PIN erforderlich.");
            return;
          }
          // 1) Reverse completed-services deltas
          const snapSet = new Set(snapshot.completed_services);
          const restoreRemove = [...postSaveCompleted].filter((k) => !snapSet.has(k));
          const restoreAdd    = [...snapSet].filter((k) => !postSaveCompleted.has(k));
          for (const k of restoreRemove) {
            await api.patch(
              `/team/bookings/${undoBookingId}/completed-services`,
              { service_key: k, done: false },
              { headers: undoHeaders },
            );
          }
          for (const k of restoreAdd) {
            await api.patch(
              `/team/bookings/${undoBookingId}/completed-services`,
              { service_key: k, done: true },
              { headers: undoHeaders },
            );
          }
          // 2) Konsolidierter PATCH auf den Snapshot-Zustand
          const { data } = await api.patch(
            `/bookings/${undoBookingId}`,
            {
              services: snapshot.services,
              disabled_standard_services: snapshot.disabled_standard_services,
              from_date: snapshot.from_date,
              to_date: snapshot.to_date,
              notes: snapshot.notes,
              service_notes: snapshot.service_notes,
              assigned_to: snapshot.assigned_to,
            },
            { headers: undoHeaders },
          );
          // Nur updaten, wenn das Modal noch dieselbe Buchung zeigt
          if (view?.id === undoBookingId) {
            setLocal((cur) => ({ ...(cur || {}), ...data }));
          }
          onChanged?.();
          bumpAudit();
          toast.success("Änderungen rückgängig gemacht.");
        } catch (err) {
          toast.error(formatErr(err.response?.data?.detail) || err.message);
        } finally {
          setGlobalSaving(false);
        }
      };

      toast.success(`${total} Änderung${total === 1 ? "" : "en"} gespeichert.`, {
        duration: 5000,
        action: {
          label: "Rückgängig",
          onClick: undoFn,
        },
      });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setGlobalSaving(false);
    }
  };

  const handleGlobalDiscard = () => {
    setPendingChecklist({ completed: null, services: null, disabled_std: null });
    setPendingAssignee(null);
    setPendingDates(null);
    setGlobalOverlapError(null);
  };

  // Feb 2026 · 16 Sep 2026 · Kunden-Save-Flow.
  // Öffnet den PIN-Dialog (Setup, wenn noch keine PIN) und feuert bei
  // Bestätigung einen einzigen PATCH /bookings/{id} mit `customer_pin` +
  // allen gepufferten Feldern. Backend liefert 428 wenn PIN fehlt,
  // 403 bei falscher PIN, 409 bei Overlap.
  const handleCustomerSave = () => {
    if (!pendingDates || globalSaving) return;
    setCustomerPinPromptMode(customerPinIsSet ? "confirm" : "setup");
  };

  const submitCustomerSave = async (pin) => {
    if (!pendingDates || !view?.id) return;
    setGlobalSaving(true);
    try {
      const payload = { customer_pin: pin };
      if (pendingDates.from_date !== undefined) payload.from_date = pendingDates.from_date;
      if (pendingDates.to_date !== undefined) payload.to_date = pendingDates.to_date;
      if (pendingDates.notes !== undefined) payload.notes = pendingDates.notes;
      if (pendingDates.services !== undefined) payload.services = pendingDates.services;
      if (pendingDates.disabled_standard_services !== undefined) payload.disabled_standard_services = pendingDates.disabled_standard_services;
      if (pendingDates.service_notes !== undefined) payload.service_notes = pendingDates.service_notes;
      try {
        const { data } = await api.patch(`/bookings/${view.id}`, payload);
        setLocal((cur) => ({ ...(cur || {}), ...data }));
        setPendingDates(null);
        setGlobalOverlapError(null);
        setCustomerPinPromptMode(null);
        onChanged?.();
        toast.success(de ? "Änderungen gespeichert." : "Changes saved.");
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        const code = typeof detail === "object" ? detail?.code : null;
        if (status === 428 || code === "customer_pin_not_set") {
          // PIN noch nicht gesetzt → in Setup-Mode wechseln (Dialog bleibt offen)
          setCustomerPinIsSet(false);
          setCustomerPinPromptMode("setup");
          toast.info(de
            ? "Bitte richten Sie zuerst Ihre persönliche 4-stellige PIN ein."
            : "Please set up your personal 4-digit PIN first.");
          return;
        }
        if (status === 403 && code === "customer_pin_wrong") {
          // Falsche PIN → Dialog bleibt offen, damit Kunde neu eingeben kann
          toast.error(de ? "PIN ist falsch. Bitte erneut versuchen." : "PIN is wrong. Please try again.");
          throw err;
        }
        if (status === 409) {
          const conflict = typeof detail === "object" ? detail : {};
          setGlobalOverlapError({
            from: conflict?.conflict_from_date || null,
            to: conflict?.conflict_to_date || null,
          });
          setCustomerPinPromptMode(null);
          toast.error(conflict?.message || (de
            ? "Zeitraum überlappt mit einer bestehenden Abwesenheit."
            : "Period overlaps with another absence."));
          return;
        }
        // Sonstige Fehler → Dialog schließen + Toast
        setCustomerPinPromptMode(null);
        toast.error(formatErr(detail) || err.message);
      }
    } finally {
      setGlobalSaving(false);
    }
  };

  const onCustomerPinSetupSuccess = () => {
    // PIN gerade neu eingerichtet — direkt in „confirm"-Modus wechseln,
    // damit der Kunde die gleiche PIN nochmal eingibt und die Änderung
    // damit signiert.
    setCustomerPinIsSet(true);
    setCustomerPinPromptMode("confirm");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      style={{ background: `${withAlpha(BRAND_COLORS.navyDark, 0.55)}` }}
      data-testid="booking-detail-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-ivory w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-gold/40">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-ivory border-b border-navy/10 px-5 md:px-7 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="label-caps mb-1">{de ? "Abwesenheit" : "Absence"}</div>
            <div className="font-display text-xl md:text-2xl text-navy leading-tight">
              {de ? DE_LONG(booking.from_date) : enDateLong(booking.from_date)}
            </div>
            <div className="text-[13px] text-navy-dark/60 leading-tight mt-1">
              → {de ? DE_LONG(booking.to_date) : enDateLong(booking.to_date)} · {nights} {de ? (nights === 1 ? "Nacht" : "Nächte") : (nights === 1 ? "night" : "nights")}
            </div>
            {propertyName && (
              <div className="text-[12px] text-navy-dark/60 mt-1">{propertyName}</div>
            )}
            {/* Feb 2026 · 11 Sep 2026 · Persistent Zweizeiler direkt unter der
                Hauptzeile (Kind · Objekt · Status). Zeigt IMMER beide Rollen:
                Haupt-Concierge des Objekts + Verantwortlicher der Buchung
                — auch wenn identisch, damit der Admin auf einen Blick weiß,
                wer generell zuständig ist und wer diese Buchung führt. */}
            {mode === "team" && (
              <BookingHeaderAssigneeLines booking={view} />
            )}
            {mode === "team" && (
              <div className="mt-2">
                <SeenStatusLine entity={{ ...(view || {}), ...(seenNow || {}) }} testid="booking-seen-status" />
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] tracking-[0.14em] uppercase rounded-full"
              style={{ background: meta.bg, color: meta.fg }}
              data-testid="booking-detail-status"
              data-status={effStatus}
            >
              {de ? meta.label : (meta.label_en || meta.label)}
            </span>
            <div className="flex items-center gap-2">
              {mode === "team" && (
                <a
                  href={`/team/print/booking/${booking.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-detail-print-btn"
                  className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.15em] uppercase border border-navy/15 hover:border-navy px-3 py-1.5 text-navy-dark/85 hover:text-navy transition-colors"
                  title="Vorbereitungs-Checkliste inkl. Präferenzen drucken"
                >
                  <Printer className="w-3 h-3" strokeWidth={1.8} /> Drucken
                </a>
              )}
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 border border-navy/15 hover:border-navy flex items-center justify-center"
                data-testid="booking-detail-close"
                aria-label={de ? "Schließen" : "Close"}
              >
                <X className="w-4 h-4 text-navy" strokeWidth={1.6} />
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 md:px-7 py-6 space-y-7">
          {/* Notes */}
          {booking.notes && (
            <div className="border-l-2 border-gold pl-4 text-[13px] text-navy-dark/80 italic leading-relaxed">
              {booking.notes}
            </div>
          )}

          {/* Team-Modus: „Präferenzen ansehen"-Pill direkt oberhalb der
              Services-Checkliste — führt zum Kundendetail mit Präferenzen-
              Karte inkl. Diff der letzten Änderung. Zusätzlich rendert die
              kompakte Prep-Card die AKTIVEN Präferenzen direkt inline, damit
              der Concierge sie ohne Klick vor der Vorbereitung sieht. */}
          {mode === "team" && booking?.customer_id && (
            <div className="space-y-2">
              <BookingPrepPreferencesCard customerId={booking.customer_id} />
              <Link
                to={`/team/customers/${booking.customer_id}#preferences`}
                data-testid="booking-detail-see-preferences-pill"
                className="inline-flex items-center gap-2 self-start text-[11px] tracking-[0.18em] uppercase border px-4 py-2 hover:opacity-80 transition-opacity"
                style={{
                  background: "transparent",
                  color: BRAND_COLORS.goldDark || "#8A7635",
                  borderColor: withAlpha(BRAND_COLORS.gold, 0.5),
                }}
                title="Zum vollständigen Kundenprofil mit Diff-Karte"
              >
                <HeartHandshake className="w-3.5 h-3.5" strokeWidth={1.6} />
                Vollständiges Profil
              </Link>
            </div>
          )}

          {/* Team-Modus: Kompakte Aufgaben-Checkliste (nur ausgewählte Services)
              + „Service hinzufügen"-Button. Ersetzt die drei Kunden-Sektionen
              (Zusatz-Liste / Deaktivierte / Wünsche), weil das Team primär
              die Aufgaben abhaken und ggfs. ergänzen möchte. */}
          {mode === "team" && (
            <TeamServicesChecklist
              booking={view}
              extras={extras}
              standardServices={standardServices}
              pending={pendingChecklist}
              setPending={setPendingChecklist}
              saving={globalSaving}
            />
          )}

          {/* Team-Modus: PRIVATE Kunden-Checkliste als READ-ONLY.
              Nur zur Sicht: was hat der Kunde selbst für seine Abreise/Anreise
              vorbereitet? Diese Punkte beeinflussen den Booking-Status
              NICHT (siehe backend `_all_booking_items_done`) — der Kalender
              wird ausschließlich durch die Team-Services grün. */}
          {mode === "team" && (dep.length > 0 || arr.length > 0) && (
            <TeamReadOnlyCustomerChecklist departure={dep} arrival={arr} />
          )}

          {/* Feb 2026 · Directive „Checklisten-Audit sichtbar in Buchungs-
              detail" (1 Sep 2026): kompakter, standardmäßig eingeklappter
              Panel. Zeigt jede Admin-Änderung (Toggle, Add/Remove Service,
              deaktiviert Inklusiv) mit Name + Zeitpunkt. Nur Team-Modus,
              weil der Endpoint admin/concierge-only ist. */}
          {mode === "team" && (
            <BookingChecklistAuditPanel bookingId={booking.id} refreshToken={auditRefresh} />
          )}
          {/* Feb 2026 · User-Directive „Datumsänderung mit Protokoll"
              (5 Sep 2026): eigener Panel neben dem Checklisten-Audit, damit
              Terminverschiebungen mit Name + Zeitstempel nachvollziehbar
              bleiben. Team-only (Kunden sehen keine internen Verlaufsdaten). */}
          {mode === "team" && (
            <BookingDateAuditPanel bookingId={booking.id} refreshToken={auditRefresh} />
          )}

          {/* Feb 2026 · Verantwortlichen-Picker (Team-Modus). Zeigt sofort
              wer die Buchung betreut, und erlaubt dem Admin die schnelle
              Neuzuweisung, ohne im Kunden-Detail nach dem Objekt-Concierge
              zu suchen. Kritisch für die Grau→Blau-Regel — ohne Assignee
              bleibt die Timeline auf Grau. */}
          {mode === "team" && (
            <BookingAssigneePicker
              booking={view}
              pending={pendingAssignee}
              setPending={setPendingAssignee}
              saving={globalSaving}
            />
          )}

          {/* Feb 2026 · Concierge-Notiz zur Willkommen-zurück-Mail
              (Team-Modus, nur wenn Buchung noch nicht abgereist ist).
              Der zugewiesene Concierge tippt einen persönlichen Einzeiler,
              der am Rückkehr-Tag als goldene Blockquote in der
              automatischen Willkommen-zurück-Mail landet — der Kunde
              spürt, dass jemand für ihn persönlich vorbereitet hat. */}
          {mode === "team" && view?.status !== "cancelled" && (
            <WelcomeBackNoteEditor
              booking={view}
              onSaved={(next) => {
                setLocal((cur) => ({ ...(cur || {}), ...next }));
                onChanged?.();
              }}
            />
          )}

          {/* Booked extras — with per-service notes */}
          {mode !== "team" && extrasNames.length > 0 && (
            <div data-testid="booking-detail-extras">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
                <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
                  {de ? "Gebuchte Zusatzleistungen" : "Booked add-on services"}
                </div>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {extrasNames.map((ex) => {
                  const note = serviceNotes[ex.id];
                  return (
                    <li
                      key={ex.id}
                      className="bg-white border border-navy/10 px-3 py-2 text-[13px] text-navy leading-snug"
                      data-testid={`booking-view-extra-${ex.key}`}
                    >
                      <div>{de ? (ex.name_de || ex.name_en) : (ex.name_en || ex.name_de)}</div>
                      {note && (
                        <div
                          className="mt-1 pl-2 border-l-2 border-gold/60 text-[11.5px] text-navy-dark/70 italic"
                          data-testid={`booking-view-extra-note-${ex.key}`}
                        >
                          {note}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Deaktivierte Inklusiv-Leistungen (nicht ausführen für diesen Termin) */}
          {disabledStdItems.length > 0 && (
            <div data-testid="booking-detail-disabled-inclusive">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-4 h-4" style={{ color: STATUS_COLORS.red }} strokeWidth={1.6} />
                <div className="label-caps" style={{ color: STATUS_COLORS.red }}>
                  {de ? "Für diesen Termin abgewählt" : "Skipped for this booking"}
                </div>
              </div>
              <ul className="space-y-1">
                {disabledStdItems.map((s) => {
                  const note = serviceNotes[s.key];
                  return (
                    <li
                      key={s.key}
                      className="text-[12.5px] text-navy-dark/70 leading-snug"
                      data-testid={`booking-view-disabled-${s.key}`}
                    >
                      <span className="line-through decoration-navy-dark/30">{de ? (s.name_de || s.name_en) : (s.name_en || s.name_de)}</span>
                      {note && (
                        <span className="ml-2 text-[11.5px] italic text-navy-dark/60">— {note}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Standard-service notes (customer added a wish for an inclusive service) */}
          {mode !== "team" && (() => {
            const stdKeysWithNotes = (standardServices || [])
              .filter((s) => !disabledStd.includes(s.key) && (serviceNotes[s.key] || "").trim())
              .slice(0, 20);
            if (stdKeysWithNotes.length === 0) return null;
            return (
              <div data-testid="booking-detail-inclusive-notes">
                <div className="flex items-center gap-2 mb-2">
                  <StickyNote className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
                  <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
                    {de ? "Wünsche zu Inklusiv-Leistungen" : "Wishes for included services"}
                  </div>
                </div>
                <ul className="space-y-1.5">
                  {stdKeysWithNotes.map((s) => (
                    <li
                      key={s.key}
                      className="text-[12.5px] text-navy-dark leading-snug"
                      data-testid={`booking-view-inclusive-note-${s.key}`}
                    >
                      <span className="text-navy-dark/70">{de ? (s.name_de || s.name_en) : (s.name_en || s.name_de)}: </span>
                      <span className="italic text-navy-dark/85">{serviceNotes[s.key]}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Departure checklist — interaktiv (customer view only) */}
          {mode !== "team" && dep.length > 0 && (
            <ChecklistSection
              icon={PlaneTakeoff}
              de={de}
              title={de ? "Abreise-Checkliste" : "Departure checklist"}
              items={dep}
              kind="departure"
              bookingId={booking.id}
              seenByTeamAt={view?.seen_by_team_at}
              seenByTeamName={view?.seen_by_team_name}
              onLocalUpdate={(items) => setLocal((b) => ({ ...b, departure_checklist: items }))}
              onChanged={onChanged}
              readOnly={effStatus === "cancelled"}
              testid="booking-detail-departure-checklist"
            />
          )}

          {/* Arrival checklist — interaktiv (customer view only) */}
          {mode !== "team" && arr.length > 0 && (
            <ChecklistSection
              icon={ClipboardCheck}
              de={de}
              title={de ? "Anreise-Checkliste" : "Arrival checklist"}
              items={arr}
              kind="arrival"
              bookingId={booking.id}
              seenByTeamAt={view?.seen_by_team_at}
              seenByTeamName={view?.seen_by_team_name}
              onLocalUpdate={(items) => setLocal((b) => ({ ...b, arrival_checklist: items }))}
              onChanged={onChanged}
              readOnly={effStatus === "cancelled"}
              testid="booking-detail-arrival-checklist"
            />
          )}

          {/* Empty-state cue if nothing configured (customer view) */}
          {mode !== "team" && extrasNames.length === 0 && dep.length === 0 && arr.length === 0 && (
            <div className="bg-white border border-dashed border-navy/20 p-6 text-center text-[13px] text-navy-dark/60">
              {de
                ? "Für diesen Termin sind noch keine Zusatzleistungen oder Checklisten hinterlegt."
                : "No add-on services or checklists are stored for this booking yet."}
            </div>
          )}
        </div>

        {/* Feb 2026 · Status-Historie am unteren Ende (vor dem Footer): dezent,
            zusammenklappbar. Zeigt jeden persistierten Status-Flip mit
            Zeitpunkt + Akteur (System für Auto-Downgrades / Cron-Cleanup). */}
        <div className="px-5 md:px-7 pb-4">
          <BookingStatusHistory history={view.status_history} />
        </div>

        {/* Footer: edit / cancel — customer only. Team has its own workflows
            (checklist + Service hinzufügen inside the panel) so we hide the
            customer footer entirely in team mode.
            Feb 2026 · Für stornierte Buchungen KOMPLETT read-only:
            weder „Termin ändern" noch „Stornieren"-Buttons — nur ein
            dezenter Hinweis, dass der Termin storniert ist. Die
            Checklisten oben werden zusätzlich via `readOnly` deaktiviert. */}
        {mode !== "team" && effStatus === "cancelled" && (
          <div
            className="sticky bottom-0 border-t border-red-attn/20 px-5 md:px-7 py-4"
            style={{ background: withAlpha(STATUS_COLORS.red, 0.06) }}
            data-testid="booking-detail-cancelled-footer"
          >
            <div className="flex items-center gap-3 text-[12.5px] leading-relaxed" style={{ color: BRAND_COLORS.navyDark }}>
              <XCircle className="w-4 h-4 shrink-0" style={{ color: STATUS_COLORS.red }} strokeWidth={1.6} />
              <div>
                <div className="label-caps mb-0.5" style={{ color: STATUS_COLORS.red }}>
                  {de ? "Termin storniert" : "Booking cancelled"}
                </div>
                <div className="text-navy-dark/70 italic">
                  {de
                    ? "Dieser Termin wurde storniert. Er ist schreibgeschützt — für einen neuen Wunsch legen Sie bitte eine neue Abwesenheit an oder schreiben Sie uns kurz."
                    : "This booking has been cancelled. It is read-only — please plan a new absence or drop us a short message for anything else."}
                </div>
              </div>
            </div>
          </div>
        )}
        {effStatus !== "cancelled" && (
        <div
          ref={editSectionRef}
          className={`${editOpen || cancelConfirmOpen ? "" : "sticky bottom-0"} bg-white border-t border-navy/10 px-5 md:px-7 py-4`}
        >
          {editOpen ? (
            <BookingEditForm
              booking={view}
              extras={extras}
              standardServices={standardServices}
              onCancel={() => setEditOpen(false)}
              onCommit={(fields) => {
                setPendingDates(fields);
                setEditOpen(false);
              }}
              saving={globalSaving}
              externalOverlap={globalOverlapError}
              onEditingDates={() => setGlobalOverlapError(null)}
            />
          ) : cancelConfirmOpen ? (
            <CancelConfirmation
              onKeep={() => setCancelConfirmOpen(false)}
              onConfirm={submitCancel}
              busy={sending}
            />
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11.5px] text-navy-dark/60 italic">
                {mode === "team"
                  ? "Termin verschoben? Ein Klick auf \u201eTermin \u00e4ndern\u201c \u2014 jede Datumsanpassung wird protokolliert."
                  : (de
                    ? "Pläne geändert? Ihr Concierge wird bei jeder Anpassung sofort informiert."
                    : "Plans changed? Your concierge is notified immediately on every adjustment.")}
              </div>
              <div className="flex items-center gap-2">
                {/* „Stornieren" nur im Customer-Modus — Team storniert via Status-Änderung. */}
                {mode !== "team" && (
                  <button
                    type="button"
                    onClick={() => setCancelConfirmOpen(true)}
                    data-testid="booking-detail-cancel-btn"
                    className="inline-flex items-center gap-2 border border-navy-dark/20 text-navy-dark/70 hover:border-red-700 hover:text-red-700 px-4 py-2.5 text-[11px] tracking-[0.18em] uppercase transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" strokeWidth={1.6} />
                    {de ? "Stornieren" : "Cancel booking"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setEditOpen(true)}
                  data-testid="booking-detail-edit-btn"
                  className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-ivory px-5 py-2.5 text-[11px] tracking-[0.18em] uppercase transition-colors"
                >
                  <Edit3 className="w-3.5 h-3.5" strokeWidth={1.6} />
                  {de ? "Termin ändern" : "Edit dates"}
                </button>
              </div>
            </div>
          )}
        </div>
        )}
      </div>
      {/* Feb 2026 · Admin-PIN-Portal: MUSS im JSX-Tree existieren, sonst
          rendert `useAdminChecklistPin` seinen createPortal-Dialog nie und
          Klicks auf die Team-Checkliste toggeln ohne PIN-Prompt (Bug
          3 Sep 2026: adminPinDialog wurde zwar destrukturiert, aber nie
          gemountet). Portal hängt sich an document.body — Placement in
          der JSX-Hierarchie ist irrelevant, Hauptsache gerendert. */}
      {adminPinDialog}

      {/* Feb 2026 · 11 Sep 2026 · Global-Save-Bar. Fixed bottom overlay,
          erscheint nur im Team-Modus, wenn irgendwo im Modal Puffer offen
          sind (Checkliste, Verantwortlicher, Termin-Editor). Ein Klick auf
          „Alle Änderungen speichern" fragt den 4-stelligen PIN einmalig ab
          und feuert alle gepufferten PATCHes im Batch. */}
      {isTeam && totalPending > 0 && (
        <div
          className="fixed left-0 right-0 bottom-0 z-[60] p-3 md:p-4 flex justify-center pointer-events-none"
          data-testid="booking-modal-global-save-bar"
          data-overlap={globalOverlapError ? "true" : undefined}
        >
          <div
            className="pointer-events-auto w-full max-w-2xl flex items-center justify-between gap-3 flex-wrap p-3 md:p-4 border shadow-2xl transition-colors"
            style={{
              background: globalOverlapError ? withAlpha(STATUS_COLORS.red, 0.06) : BRAND_COLORS.ivory,
              borderColor: globalOverlapError ? STATUS_COLORS.red : BRAND_COLORS.gold,
              boxShadow: globalOverlapError
                ? `0 20px 40px -12px ${withAlpha(STATUS_COLORS.red, 0.35)}`
                : undefined,
            }}
          >
            <div className="text-[12.5px] text-navy-dark leading-snug">
              <span
                className="font-medium text-navy"
                data-testid="booking-modal-global-save-count"
              >
                {totalPending}
              </span>
              {" "}
              Änderung{totalPending === 1 ? "" : "en"} · noch nicht gespeichert
              <div className="text-[11px] text-navy-dark/60 mt-0.5 leading-snug flex flex-wrap gap-x-3 gap-y-0.5">
                {checklistCount > 0 && (
                  <span data-testid="booking-modal-global-save-breakdown-checklist">
                    Checkliste: {checklistCount}
                  </span>
                )}
                {assigneeCount > 0 && (
                  <span data-testid="booking-modal-global-save-breakdown-assignee">
                    Verantwortlicher: {assigneeCount}
                  </span>
                )}
                {datesCount > 0 && (
                  <span data-testid="booking-modal-global-save-breakdown-dates">
                    Termin: {datesCount}
                  </span>
                )}
              </div>
              {globalOverlapError && (
                <div
                  className="mt-1.5 flex items-center gap-1.5 text-[11.5px] font-medium leading-snug"
                  style={{ color: STATUS_COLORS.red }}
                  data-testid="booking-modal-global-save-overlap"
                  role="alert"
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
                  <span>
                    {globalOverlapError.from && globalOverlapError.to ? (
                      <>
                        Konflikt vom{" "}
                        <span data-testid="booking-modal-global-save-overlap-from">
                          {deDateMedium(globalOverlapError.from)}
                        </span>
                        {" → "}
                        <span data-testid="booking-modal-global-save-overlap-to">
                          {deDateMedium(globalOverlapError.to)}
                        </span>
                      </>
                    ) : (
                      "Zeitraum überlappt mit einer bestehenden Abwesenheit."
                    )}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleGlobalDiscard}
                disabled={globalSaving}
                data-testid="booking-modal-global-discard-btn"
                className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy px-3 py-2 disabled:opacity-40"
              >
                Verwerfen
              </button>
              <button
                type="button"
                onClick={handleGlobalSave}
                disabled={globalSaving || !!globalOverlapError}
                data-testid="booking-modal-global-save-btn"
                title={globalOverlapError ? "Bitte Zeitraum anpassen — Konflikt mit bestehender Abwesenheit." : undefined}
                className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
                style={{ background: globalOverlapError ? STATUS_COLORS.red : BRAND_COLORS.navy }}
              >
                {globalSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
                Alle Änderungen speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 · 16 Sep 2026 · Customer-Save-Bar.
          Erscheint im Kunden-Modus, sobald der Kunde in „Termin ändern"
          etwas übernommen hat. Ein Klick auf „Speichern" öffnet den
          Customer-PIN-Dialog; die PIN wandert als `customer_pin` in den
          PATCH-Body. Kein Batch nötig (Kunde ändert nur ein Feld-Set). */}
      {!isTeam && mode !== "team" && pendingDates !== null && effStatus !== "cancelled" && (
        <div
          className="fixed left-0 right-0 bottom-0 z-[60] p-3 md:p-4 flex justify-center pointer-events-none"
          data-testid="customer-booking-save-bar"
          data-overlap={globalOverlapError ? "true" : undefined}
        >
          <div
            className="pointer-events-auto w-full max-w-2xl flex items-center justify-between gap-3 flex-wrap p-3 md:p-4 border shadow-2xl transition-colors"
            style={{
              background: globalOverlapError ? withAlpha(STATUS_COLORS.red, 0.06) : BRAND_COLORS.ivory,
              borderColor: globalOverlapError ? STATUS_COLORS.red : BRAND_COLORS.gold,
              boxShadow: globalOverlapError
                ? `0 20px 40px -12px ${withAlpha(STATUS_COLORS.red, 0.35)}`
                : undefined,
            }}
          >
            <div className="text-[12.5px] text-navy-dark leading-snug">
              <span
                className="font-medium text-navy"
                data-testid="customer-booking-save-count"
              >
                {datesCount}
              </span>
              {" "}
              {de
                ? `Änderung${datesCount === 1 ? "" : "en"} · noch nicht gespeichert`
                : `change${datesCount === 1 ? "" : "s"} · not yet saved`}
              <div className="text-[11px] text-navy-dark/60 mt-0.5 leading-snug flex items-center gap-1.5">
                <ShieldCheck className="w-3 h-3" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.8} />
                {de
                  ? "Mit Ihrer persönlichen PIN bestätigen"
                  : "Confirm with your personal PIN"}
              </div>
              {globalOverlapError && (
                <div
                  className="mt-1.5 flex items-center gap-1.5 text-[11.5px] font-medium leading-snug"
                  style={{ color: STATUS_COLORS.red }}
                  data-testid="customer-booking-save-overlap"
                  role="alert"
                >
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
                  <span>
                    {globalOverlapError.from && globalOverlapError.to ? (
                      <>
                        {de ? "Konflikt vom " : "Conflict from "}
                        <span data-testid="customer-booking-save-overlap-from">
                          {deDateMedium(globalOverlapError.from)}
                        </span>
                        {" → "}
                        <span data-testid="customer-booking-save-overlap-to">
                          {deDateMedium(globalOverlapError.to)}
                        </span>
                      </>
                    ) : (
                      de ? "Zeitraum überlappt mit einer bestehenden Abwesenheit."
                        : "Period overlaps with another absence."
                    )}
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleGlobalDiscard}
                disabled={globalSaving}
                data-testid="customer-booking-discard-btn"
                className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy px-3 py-2 disabled:opacity-40"
              >
                {de ? "Verwerfen" : "Discard"}
              </button>
              <button
                type="button"
                onClick={handleCustomerSave}
                disabled={globalSaving || !!globalOverlapError}
                data-testid="customer-booking-save-btn"
                title={globalOverlapError
                  ? (de ? "Bitte Zeitraum anpassen — Konflikt mit bestehender Abwesenheit." : "Please adjust the period — conflict with existing absence.")
                  : undefined}
                className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
                style={{ background: globalOverlapError ? STATUS_COLORS.red : BRAND_COLORS.navy }}
              >
                {globalSaving && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
                {de ? "Speichern & bestätigen" : "Save & confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 · 16 Sep 2026 · Customer-PIN-Dialog. Wird von
          `handleCustomerSave` geöffnet, ruft `submitCustomerSave(pin)` beim
          Confirm. Bei fehlender PIN wechselt der Dialog in Setup-Mode und
          nach dem Setup zurück in Confirm-Mode (siehe `onCustomerPinSetupSuccess`). */}
      {mode !== "team" && (
        <CustomerPinPromptDialog
          open={!!customerPinPromptMode}
          mode={customerPinPromptMode || "confirm"}
          de={de}
          title={customerPinPromptMode === "setup"
            ? (de ? "PIN einrichten" : "Set up PIN")
            : (de ? "Änderungen bestätigen" : "Confirm changes")}
          description={customerPinPromptMode === "setup"
            ? (de
                ? "Bevor Sie Buchungen ändern können, benötigen Sie eine persönliche 4-stellige PIN. Sie dient als digitale Signatur."
                : "Before you can change bookings, set up a personal 4-digit PIN. It serves as your digital signature.")
            : (de
                ? "Bitte bestätigen Sie Ihre Änderung mit Ihrer persönlichen 4-stelligen PIN."
                : "Please confirm your change with your personal 4-digit PIN.")}
          submitLabel={customerPinPromptMode === "confirm"
            ? (de ? "Speichern" : "Save")
            : undefined}
          onConfirm={submitCustomerSave}
          onSuccess={onCustomerPinSetupSuccess}
          onClose={() => { if (!globalSaving) setCustomerPinPromptMode(null); }}
        />
      )}
    </div>
  );
}

